import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
} from "react";
import { HighlightArea, highlightPlugin, RenderHighlightsProps, HighlightPlugin } from "@react-pdf-viewer/highlight";

export type ICitationHighlightItemType = "CASE_OVERVIEW" | "CASEY" | "CLINICAL_EVALUATION" | "AVOIDABLE_READMISSION";

export interface ICitationHighlightItem {
  type: ICitationHighlightItemType;
  externalId: string;
  attachmentId: string;
  highlightArea: HighlightArea;
  spanText?: string;
}

interface ReviewAIHighlightProviderProps {
  children: React.ReactNode;
}

export interface ReviewAIHighlightContextType {
  setAttachmentId: Dispatch<SetStateAction<string | undefined>>;
  reviewAIHighlightPluginInstance: HighlightPlugin | undefined;
  hightlightCitationItems: ({
    items,
    attachmentId,
    faxAttachmentId,
  }: {
    items: ICitationHighlightItem[];
    attachmentId: string;
    faxAttachmentId: string;
  }) => void;
  selectedCitationId: string | undefined;
  setSelectedCitationId: Dispatch<SetStateAction<string | undefined>>;
  hoveredChatbotCitationId: string | undefined;
  setHoveredChatbotCitationId: Dispatch<SetStateAction<string | undefined>>;
  onReviewAIDocumentLoad: (attachmentId: string | undefined) => void;
}

const defaultState: ReviewAIHighlightContextType = {
  setAttachmentId: () => {},
  reviewAIHighlightPluginInstance: undefined,
  hightlightCitationItems: () => {},
  selectedCitationId: undefined,
  setSelectedCitationId: () => {},
  hoveredChatbotCitationId: undefined,
  setHoveredChatbotCitationId: () => {},
  onReviewAIDocumentLoad: () => {},
};

export const ReviewAIHighlightContext = createContext<ReviewAIHighlightContextType>(defaultState);

export const ReviewAIHighlightProvider = ({ children }: ReviewAIHighlightProviderProps) => {
  const [highlightedCitationItems, setHighlightedCitationItems] = useState<ICitationHighlightItem[]>([]);

  const highlightedCitationItemsRef = useRef<ICitationHighlightItem[]>([]);

  const [attachmentId, setAttachmentId] = useState<string | undefined>();

  const [selectedCitationId, setSelectedCitationId] = useState<string | undefined>(undefined);
  const [hoveredChatbotCitationId, setHoveredChatbotCitationId] = useState<string | undefined>(undefined);

  const highlightedAttachmentId = useRef<string | undefined>(undefined);
  const highlightedFaxAttachmentId = useRef<string | undefined>(undefined);
  const highlightedArea = useRef<HighlightArea | undefined>(undefined);
  const alreadyJumped = useRef<boolean>(false);

  const getHighlights = useCallback(() => {
    return highlightedCitationItems.filter((highlight) => attachmentId && highlight.attachmentId === attachmentId);
  }, [highlightedCitationItems, attachmentId]);

  const renderHighlights = (props: RenderHighlightsProps) => (
    <div>
      {getHighlights()
        .filter((highlight) => highlight.highlightArea.pageIndex === props.pageIndex)
        .map((highlight, idx) => (
          <React.Fragment key={`${highlight.attachmentId}-${idx}`}>
            <div
              key={idx}
              className="highlight"
              style={Object.assign(
                {},
                {
                  mixBlendMode: "multiply",
                  zIndex: 0,
                  border: "2px solid #039EC3",
                  borderRadius: "4px",
                },
                props.getCssProperties(highlight.highlightArea, props.rotation)
              )}
            />
          </React.Fragment>
        ))}
    </div>
  );

  const reviewAIHighlightPluginInstance = highlightPlugin({
    renderHighlights,
  });

  const { jumpToHighlightArea } = reviewAIHighlightPluginInstance;

  // The plugin instance is rebuilt on every render, so jumpToHighlightArea is a new function each
  // time. Read it from a ref below instead of depending on it: hover and selection state live in this
  // provider, so a re-render inside the jump's 500ms window would otherwise cancel and re-arm the
  // timer, and a few of them in a row would starve the jump entirely.
  const jumpToHighlightAreaRef = useRef(jumpToHighlightArea);

  useEffect(() => {
    jumpToHighlightAreaRef.current = jumpToHighlightArea;
  }, [jumpToHighlightArea]);

  function hightlightCitationItems({
    items,
    attachmentId: actualAttachmentId,
    faxAttachmentId: actualFaxAttachmentId,
  }: {
    items: ICitationHighlightItem[];
    attachmentId: string;
    faxAttachmentId: string;
  }) {
    highlightedCitationItemsRef.current = items;

    if (items.length === 0) {
      setHighlightedCitationItems([]);
      return;
    }

    const firstItem = items[0];
    const firstHighlightArea: HighlightArea = firstItem.highlightArea;

    // This is to give some space and not scroll exactly to the top value of the first highlight
    const eligibleTopValuesToJumpTo: number[] = [
      firstHighlightArea.top - 2,
      firstHighlightArea.top - 5,
      firstHighlightArea.top - 10,
      firstHighlightArea.top - 20,
    ].filter((value) => value >= 0);

    const topValueToJumpTo: number = eligibleTopValuesToJumpTo.length > 0 ? Math.min(...eligibleTopValuesToJumpTo) : 0;

    const hightlightAreaToJumpTo: HighlightArea = {
      ...firstHighlightArea,
      top: topValueToJumpTo,
    };

    const isSameFaxAttachment = actualFaxAttachmentId && actualFaxAttachmentId === attachmentId;
    const isSameAttachment = actualAttachmentId === attachmentId;
    if (isSameAttachment || isSameFaxAttachment) {
      setHighlightedCitationItems(items);
      jumpToHighlightArea(hightlightAreaToJumpTo);
      alreadyJumped.current = true;
    } else {
      alreadyJumped.current = false;
    }

    highlightedAttachmentId.current = actualAttachmentId;
    highlightedFaxAttachmentId.current = actualFaxAttachmentId;
    highlightedArea.current = hightlightAreaToJumpTo;
  }

  // Every attachmentId that has ever fired a real onDocumentLoad — lets us tell a fresh
  // load (still in flight, not in this set) apart from a cached revisit (already in it).
  const loadedAttachmentIdsRef = useRef<Set<string>>(new Set());
  const [lastLoadedAttachmentId, setLastLoadedAttachmentId] = useState<string | undefined>(undefined);

  const matchesPendingHighlight = useCallback((candidateAttachmentId: string | undefined) => {
    if (!candidateAttachmentId) {
      return false;
    }
    return (
      candidateAttachmentId === highlightedAttachmentId.current ||
      (!!highlightedFaxAttachmentId.current && highlightedFaxAttachmentId.current === candidateAttachmentId)
    );
  }, []);

  // The real "PDF finished loading" signal, wired to the underlying pdf viewer's onDocumentLoad.
  const onReviewAIDocumentLoad = useCallback((loadedAttachmentId: string | undefined) => {
    if (!loadedAttachmentId) {
      return;
    }
    loadedAttachmentIdsRef.current.add(loadedAttachmentId);
    setLastLoadedAttachmentId(loadedAttachmentId);
  }, []);

  // Applies the pending highlight once the target attachment is confirmed loaded: either a fresh
  // load just finished (lastLoadedAttachmentId, driven by the real onDocumentLoad event), or the
  // displayed attachment switched to one that was already loaded before (cached, so
  // onDocumentLoad won't fire again — we fall back to the eager attachmentId switch instead).
  useEffect(() => {
    if (alreadyJumped.current || !highlightedArea.current) {
      return;
    }

    const freshLoadReady = matchesPendingHighlight(lastLoadedAttachmentId);
    const cachedDisplayReady =
      !!attachmentId && matchesPendingHighlight(attachmentId) && loadedAttachmentIdsRef.current.has(attachmentId);

    if (!freshLoadReady && !cachedDisplayReady) {
      return;
    }

    // Even once loaded/displayed, the pdf viewer needs a tick before it can jump/scroll reliably.
    const timer = setTimeout(() => {
      if (highlightedArea.current && !alreadyJumped.current) {
        setHighlightedCitationItems(highlightedCitationItemsRef.current);
        jumpToHighlightAreaRef.current(highlightedArea.current);
        alreadyJumped.current = true;
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [attachmentId, lastLoadedAttachmentId, matchesPendingHighlight]);

  return (
    <ReviewAIHighlightContext.Provider
      value={{
        setAttachmentId,
        reviewAIHighlightPluginInstance,
        hightlightCitationItems,
        selectedCitationId,
        setSelectedCitationId,
        hoveredChatbotCitationId,
        setHoveredChatbotCitationId,
        onReviewAIDocumentLoad,
      }}
    >
      {children}
    </ReviewAIHighlightContext.Provider>
  );
};

export const useReviewAIHighlightContext = () => {
  const context = useContext(ReviewAIHighlightContext);
  if (!context) {
    return defaultState;
  }
  return context;
};
